import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { AuctionCard } from "@/components/AuctionCard";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { ArrowRight, Gavel, Store, Layers, LayoutDashboard, Megaphone, Sparkles, GraduationCap } from "lucide-react";

const HERO_WIDE = "https://images.unsplash.com/photo-1530046614490-89e6f776b83b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [auctions, setAuctions] = useState([]);

  useEffect(() => {
    api.get("/auctions", { params: { sort: "ending" } }).then(({ data }) => setAuctions(data.auctions.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0E14] overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <img src={HERO_WIDE} alt="Auction floor" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, rgba(8,10,14,0.72) 0%, rgba(8,10,14,0.90) 55%, rgba(8,10,14,0.98) 100%)" }} />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0B0E14] to-transparent" />
        <div className="absolute inset-0 grain opacity-60" />

        <div className="relative z-10 max-w-3xl mx-auto w-full px-4 sm:px-6 text-center pb-20 pt-28">
          <motion.div initial={{ opacity: 0, scale: 0.8, y: -10 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.7, ease: "easeOut" }} className="flex justify-center mb-8">
            <div className="relative">
              <div className="absolute inset-0 -m-8 rounded-full bg-amber-400/25 blur-3xl" />
              <img src="/hero-logo.png" alt="Hustlers Hall" data-testid="hero-logo" className="relative w-52 sm:w-64 lg:w-72 drop-shadow-[0_8px_40px_rgba(250,204,21,0.35)]" />
            </div>
          </motion.div>
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="text-amber-400 text-sm sm:text-base font-semibold tracking-wide mb-4">
            The auction house for hustlers. A better eBay.
          </motion.p>

          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
            className="uppercase leading-[0.9] text-white"
            style={{ fontFamily: "Anton, sans-serif", fontSize: "clamp(2.6rem, 7vw, 5.2rem)", letterSpacing: "-0.005em" }}>
            List it. Bid it.<span className="text-amber-500"> Flip it.</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
            className="mt-6 mx-auto max-w-xl text-base sm:text-lg text-slate-300/90 leading-relaxed">
            A single watch or a whole estate — snap a photo, let AI write the listing, and run a 3-day auction.
            Free to list. One flat <span className="text-white font-semibold">4%</span> only when it sells. Open your own storefront and let a whole crew of vendors sell under your banner.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.38 }} className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <Button data-testid="hero-browse-btn" onClick={() => navigate("/marketplace")} className="bg-amber-500 hover:bg-amber-400 text-[#08110c] font-bold rounded-full h-12 px-8 text-base">
              Browse live auctions <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
            <Button data-testid="hero-sell-btn" onClick={() => navigate(user ? "/sell" : "/register")} variant="outline" className="rounded-full h-12 px-8 text-base border-white/25 bg-white/5 text-white hover:bg-white/10 backdrop-blur-sm">
              Sell something
            </Button>
            {user?.role === "admin" && (
              <Button data-testid="hero-dashboard-btn" onClick={() => navigate("/admin")} variant="outline" className="rounded-full h-12 px-8 text-base border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20">
                <LayoutDashboard className="h-4 w-4 mr-1.5" /> Dashboard
              </Button>
            )}
          </motion.div>

          <div className="mt-9 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-400">
            <span><span className="text-white font-semibold">Free</span> to list</span>
            <span className="text-slate-700">·</span>
            <span>Flat <span className="text-white font-semibold">4%</span> on sale</span>
            <span className="text-slate-700">·</span>
            <span><span className="text-white font-semibold">3-day</span> auctions</span>
          </div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="border-y border-white/5 bg-[#0d1117]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid sm:grid-cols-3 gap-8">
          {[
            [Layers, "Whole estates, easy", "Listing a garage, a collection, or a storage unit? Add a pile of photos and AI drafts the whole lot for you."],
            [Store, "Your own storefront", "Open a shop like Joe's Flea Market. Share a join code and let every vendor list under your banner."],
            [Sparkles, "AI does the typing", "Snap a photo and get a title, description, category and a smart starting price in seconds."],
          ].map(([Icon, t, b]) => (
            <div key={t} className="flex flex-col gap-2">
              <Icon className="h-6 w-6 text-amber-400" />
              <h3 className="text-white font-semibold text-lg">{t}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Campus promo */}
      <section className="bg-[#0B0E14] border-b border-amber-500/15 py-14 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-8 justify-between">
          <div className="max-w-xl">
            <div className="flex items-center gap-2 text-amber-400 text-xs font-semibold uppercase tracking-wider mb-2">
              <GraduationCap className="h-4 w-4" /> Hustler's Campus
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white">Learn to source, flip and sell.</h2>
            <p className="text-slate-400 mt-2">Micro-courses on reselling, AI tools and sourcing estate lots. Publish your own and keep 98% of every enrollment.</p>
          </div>
          <Button data-testid="landing-campus-btn" onClick={() => navigate("/campus")} className="bg-amber-500 hover:bg-amber-400 text-[#0B0E14] font-bold rounded-full h-12 px-8 shrink-0">
            Explore the Campus
          </Button>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-10">How it works</h2>
        <div className="space-y-8">
          {[
            ["01", "Snap & list — free", "Photograph a single item or a whole estate lot. AI writes the listing and suggests a starting price. Set it live in under a minute."],
            ["02", "Buyers bid for 3 days", "Watchers compete in a classic ascending auction. A last-second bid auto-extends the clock so nobody gets sniped."],
            ["03", "Winner pays, you keep 96%", "The winning bidder checks out on the site. We take a flat 4%, the rest is yours."],
          ].map(([n, t, b]) => (
            <motion.div key={n} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              className="flex gap-5 sm:gap-8 items-start border-b border-white/5 pb-8 last:border-0">
              <div className="font-mono text-3xl sm:text-4xl font-bold text-amber-500/80 shrink-0 w-14">{n}</div>
              <div>
                <h3 className="text-lg font-semibold text-white">{t}</h3>
                <p className="text-slate-400 mt-1 max-w-2xl leading-relaxed">{b}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Live auctions */}
      {auctions.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">Closing soon</h2>
              <p className="text-slate-500 text-sm mt-1">Live auctions ending next.</p>
            </div>
            <Button data-testid="landing-view-all" onClick={() => navigate("/marketplace")} variant="ghost" className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10">
              See all <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {auctions.map((a, i) => <AuctionCard key={a.auction_id} auction={a} index={i} />)}
          </div>
        </section>
      )}

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-5">
          <h3 className="text-xs font-semibold text-amber-300/90 uppercase tracking-wider mb-2">Buy &amp; sell with confidence</h3>
          <p className="text-sm text-slate-400 leading-relaxed" data-testid="risk-disclaimer">
            Bids are binding. Describe items honestly and ship what you sell. Sellers who are deceptive or reported for
            shady dealing are banned indefinitely. Payments are processed securely on the site.
          </p>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 text-center text-sm text-slate-600">
        Hustler's Hall · Free to list · a flat 4% only when your item sells.
      </footer>
    </div>
  );
}
